import { useEffect, useState } from "react";
import { Play, Loader2, Clock, GitBranch, Zap, AlertTriangle, CheckCircle2, MessageSquare, RotateCcw, Search, User, X } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const END = {
  completed: ["Percorso concluso: l'automazione termina qui.", "#10B981", CheckCircle2],
  waiting: ["L'automazione aspetta la risposta del cliente.", "#3B82F6", MessageSquare],
  dead_end: ["Collegamento mancante: il percorso si interrompe senza un nodo successivo.", "#F59E0B", AlertTriangle],
  loop: ["Attenzione: il percorso continua all'infinito (ciclo).", "#EF4444", AlertTriangle],
};

function ContactPicker({ contact, onPick }) {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (q.trim().length < 2) { setHits([]); return; }
    const t = setTimeout(() => {
      setBusy(true);
      api.get("/contacts", { params: { search: q.trim() } }).then((r) => setHits((r.data || []).slice(0, 8))).catch(() => setHits([])).finally(() => setBusy(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  if (contact) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-[#5BC65F]/50 bg-[#5BC65F]/10 px-2.5 py-1.5" data-testid="sim-contact-chip">
        <User className="h-3.5 w-3.5 text-[#2F7D32]" />
        <span className="text-xs text-slate-800 font-medium truncate">{contact.name || "Senza nome"}</span>
        <span className="text-xs text-slate-500 truncate">{contact.phone}</span>
        <span className="ml-auto text-[10px] uppercase tracking-wide text-[#2F7D32] font-semibold">cliente reale</span>
        <button type="button" onClick={() => onPick(null)} data-testid="sim-contact-clear" className="h-5 w-5 flex items-center justify-center text-slate-500 hover:text-slate-800" title="Torna al contatto di prova"><X className="h-3.5 w-3.5" /></button>
      </div>
    );
  }
  return (
    <div className="relative">
      <div className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 h-9">
        <Search className="h-3.5 w-3.5 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Simula con un cliente della rubrica… (nome o numero)" data-testid="sim-contact-search"
               className="flex-1 text-sm outline-none bg-transparent text-slate-800 placeholder:text-slate-400" />
        {busy && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
      </div>
      {hits.length > 0 && (
        <div className="absolute z-10 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg max-h-56 overflow-y-auto" data-testid="sim-contact-results">
          {hits.map((c) => (
            <button key={c.id} type="button" onClick={() => { onPick(c); setQ(""); setHits([]); }} data-testid={`sim-contact-hit-${c.id}`}
                    className="w-full text-left px-3 py-1.5 hover:bg-slate-50 flex items-center gap-2">
              <span className="text-sm text-slate-800 truncate">{c.name || "Senza nome"}</span>
              <span className="text-xs text-slate-500 ml-auto shrink-0">{c.phone}</span>
            </button>
          ))}
        </div>
      )}
      {q.trim().length >= 2 && !busy && !hits.length && <p className="text-[11px] text-slate-400 mt-1" data-testid="sim-contact-none">Nessun cliente trovato</p>}
    </div>
  );
}

export function PathSimulatorDialog({ auto, open, onClose }) {
  const [answers, setAnswers] = useState([]);
  const [contact, setContact] = useState(null);
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => { if (open) { setAnswers([]); setRes(null); setContact(null); } }, [open]);
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.post("/automations/simulate-path", { steps: auto.steps || [], start_key: auto.start_key || auto.steps?.[0]?.key || "", answers, contact_id: contact?.id || null })
      .then((r) => setRes(r.data)).catch(() => setRes(null)).finally(() => setLoading(false));
  }, [open, answers, auto, contact]);
  const [endText, endColor, EndIcon] = END[res?.end] || END.completed;
  const fields = Object.entries(res?.contact?.fields || {});
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white border-slate-200 text-slate-800 max-w-lg z-[80] [&~div]:z-[70]" data-testid="path-simulator-dialog">
        <DialogHeader><DialogTitle className="text-slate-900 text-lg flex items-center gap-2"><Play className="h-4 w-4 text-[#5BC65F]" /> Simulatore percorso</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 -mt-2">{contact ? <>Anteprima con i dati veri di <b>{contact.name || contact.phone}</b>, senza inviare nulla.</> : <>Anteprima dell'intera sequenza con un cliente di prova (Mario Rossi), senza inviare nulla.</>} Clicca un'opzione per rispondere come farebbe il cliente.</p>
        <ContactPicker contact={contact} onPick={(c) => { setContact(c); setAnswers([]); }} />
        {contact && fields.length > 0 && (
          <div className="flex flex-wrap gap-1" data-testid="sim-contact-fields">
            {fields.map(([k, v]) => <span key={k} className="rounded-full bg-slate-100 text-slate-600 text-[10px] px-2 py-0.5"><span className="text-slate-400">{k}:</span> {String(v)}</span>)}
          </div>
        )}
        {res?.cycle && (
          <div className="rounded-lg bg-[#EF4444]/10 border border-[#EF4444]/30 p-2 text-xs text-[#B91C1C] flex items-start gap-2" data-testid="path-simulator-cycle">
            <AlertTriangle className="h-4 w-4 shrink-0" /> Ciclo automatico tra gli step {res.cycle.join(" → ")}: il salvataggio verrà bloccato finché non lo interrompi.
          </div>
        )}
        <div className="max-h-[55vh] overflow-y-auto space-y-2 rounded-xl bg-[#ECE5DD] p-3" data-testid="path-simulator-sequence">
          {loading && !res && <Loader2 className="h-5 w-5 animate-spin text-slate-500 mx-auto" />}
          {res?.sequence.map((it, i) => (
            <div key={`${it.step_key}-${i}`} className="space-y-1" data-testid={`sim-step-${it.step_key}-${i}`}>
              {it.kind === "wait" || it.kind === "condition" ? (
                <p className="text-[11px] text-slate-600 text-center flex items-center justify-center gap-1">
                  {it.kind === "wait" ? <Clock className="h-3 w-3" /> : <GitBranch className="h-3 w-3" />} {it.note} <span className="text-slate-400">· {it.step_key}</span>
                </p>
              ) : (
                <>
                  {it.text && (
                    <div className="bg-white rounded-xl rounded-tl-sm px-3 py-2 max-w-[85%] shadow-sm">
                      <p className="text-sm text-slate-800 whitespace-pre-wrap">{it.text}</p>
                      <p className="text-[10px] text-slate-400 mt-1">nodo {it.step_key}{it.updates?.length ? ` · aggiorna: ${it.updates.join(", ")}` : ""}</p>
                    </div>
                  )}
                  {it.template && (
                    <div className="bg-white rounded-xl rounded-tl-sm px-3 py-2 max-w-[85%] shadow-sm border-l-4 border-[#8B5CF6]">
                      <p className="text-[10px] text-[#8B5CF6] font-semibold">Template «{it.template.name}»</p>
                      <p className="text-sm text-slate-800 whitespace-pre-wrap">{it.template.text}</p>
                    </div>
                  )}
                  {it.action && it.action !== "template" && <p className="text-[11px] text-slate-600 flex items-center gap-1"><Zap className="h-3 w-3 text-[#F59E0B]" /> Azione: {it.action}</p>}
                  {it.options?.length > 0 && !it.answer && (
                    <div className="flex flex-wrap gap-1.5 justify-end">
                      {it.options.map((o) => (
                        <button key={o} type="button" onClick={() => setAnswers([...answers, o])} data-testid={`sim-option-${o}`}
                                className="rounded-full bg-[#DCF8C6] hover:bg-[#c5f0a7] text-slate-800 text-xs px-3 py-1 shadow-sm">{o}</button>
                      ))}
                    </div>
                  )}
                  {it.answer && <div className="flex justify-end"><div className="bg-[#DCF8C6] rounded-xl rounded-tr-sm px-3 py-1.5 text-sm text-slate-800 shadow-sm">{it.answer}</div></div>}
                  {it.note && !it.stop && <p className="text-[11px] text-slate-500 text-right">{it.note}</p>}
                </>
              )}
            </div>
          ))}
          {res && (
            <p className="text-xs font-semibold flex items-center justify-center gap-1 pt-1" style={{ color: endColor }} data-testid={`path-simulator-end-${res.end}`}>
              <EndIcon className="h-3.5 w-3.5" /> {endText}
            </p>
          )}
        </div>
        <div className="flex justify-between items-center">
          <span className="text-[11px] text-slate-500">{res ? `${res.steps_visited} passaggi` : ""}</span>
          <Button type="button" variant="outline" size="sm" onClick={() => setAnswers([])} disabled={!answers.length} data-testid="path-simulator-reset" className="h-8"><RotateCcw className="h-3.5 w-3.5 mr-1" /> Ricomincia</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
